import type { VoiceCallPeer, VoiceCallSession } from '../types/voice-call'
import {
  getCachedChatUser,
  getCachedDirectoryUser,
  resolveDirectoryUser,
} from '../services/chat-service'

function normalizeName(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase()
}

function currentUserDisplayName(currentUserId: string): string | null {
  const me = getCachedChatUser()
  if (!me || me.userId !== currentUserId) return null
  const name = me.displayName.trim()
  return name || null
}

function isSelfDisplayName(displayName: string, currentUserId: string): boolean {
  const mine = currentUserDisplayName(currentUserId)
  if (!mine) return false
  return normalizeName(displayName) === normalizeName(mine)
}

function isSelfUserId(userId: string | undefined, currentUserId: string): boolean {
  return Boolean(userId && userId === currentUserId)
}

function inferLocalRole(
  session: Pick<VoiceCallSession, 'callerId' | 'calleeId' | 'phase' | 'localRole'>,
  currentUserId: string,
): VoiceCallSession['localRole'] | null {
  if (session.localRole) return session.localRole
  if (session.callerId === currentUserId) return 'caller'
  if (session.calleeId === currentUserId) return 'callee'
  if (session.phase === 'outgoing') return 'caller'
  if (session.phase === 'incoming') return 'callee'
  return null
}

export function getRemoteCallPeerId(
  session: Pick<
    VoiceCallSession,
    'callerId' | 'calleeId' | 'peer' | 'phase' | 'localRole' | 'transferRoom' | 'transfer'
  >,
  currentUserId: string,
): string {
  const role = inferLocalRole(session, currentUserId)

  // Transferror during a blind transfer — show the transfer target, not the held party.
  if (
    session.transfer?.holdingPeer &&
    session.transfer.targetUserId &&
    !isSelfUserId(session.transfer.targetUserId, currentUserId)
  ) {
    return session.transfer.targetUserId
  }

  // Transfer target: callerId is rewritten to the held/original counterparty on
  // the room INVITE — prefer that over the transferror's signal.callerId.
  if (session.transferRoom && role === 'callee' && !isSelfUserId(session.callerId, currentUserId)) {
    return session.callerId
  }

  if (
    session.transferRoom &&
    session.peer?.userId &&
    !isSelfUserId(session.peer.userId, currentUserId)
  ) {
    return session.peer.userId
  }

  if (role === 'caller') return session.calleeId
  if (role === 'callee') return session.callerId

  const peerId = session.peer.userId
  if (peerId && !isSelfUserId(peerId, currentUserId)) return peerId

  if (!isSelfUserId(session.callerId, currentUserId)) return session.callerId
  if (!isSelfUserId(session.calleeId, currentUserId)) return session.calleeId

  return session.callerId === currentUserId ? session.calleeId : session.callerId
}

function directoryPeer(userId: string, currentUserId: string): VoiceCallPeer | null {
  if (isSelfUserId(userId, currentUserId)) return null

  const cached = getCachedDirectoryUser(userId)
  if (!cached?.displayName?.trim()) return null
  if (isSelfDisplayName(cached.displayName, currentUserId)) return null

  return {
    userId,
    displayName: cached.displayName,
    avatarUrl: cached.avatarUrl,
  }
}

function sessionPeerIfRemote(
  session: VoiceCallSession,
  remoteUserId: string,
  currentUserId: string,
): VoiceCallPeer | null {
  if (
    isSelfUserId(session.peer.userId, currentUserId) ||
    session.peer.userId !== remoteUserId ||
    !session.peer.displayName.trim() ||
    isSelfDisplayName(session.peer.displayName, currentUserId)
  ) {
    return null
  }
  return session.peer
}

/**
 * A ring-group dial (urgent leadership ring) shows the GROUP, not whichever
 * member happened to seed `calleeId` ("Ania", owner report 2026-11-03). The
 * label is cleared by the ACCEPT handler, so once someone answers the normal
 * person resolution below takes over.
 */
function ringGroupPeer(session: VoiceCallSession): VoiceCallPeer | null {
  const name = session.ringGroupName?.trim()
  if (!name) return null
  return { userId: `ring-group:${session.callId}`, displayName: name, avatarUrl: null }
}

export function resolveRemoteCallPeerSync(
  session: VoiceCallSession,
  currentUserId: string,
): VoiceCallPeer {
  const group = ringGroupPeer(session)
  if (group) return group

  const remoteUserId = getRemoteCallPeerId(session, currentUserId)

  return (
    directoryPeer(remoteUserId, currentUserId) ??
    sessionPeerIfRemote(session, remoteUserId, currentUserId) ?? {
      userId: remoteUserId,
      displayName: 'Unknown',
      avatarUrl: undefined,
    }
  )
}

export async function resolveRemoteCallPeer(
  session: VoiceCallSession,
  currentUserId: string,
): Promise<VoiceCallPeer> {
  const group = ringGroupPeer(session)
  if (group) return group

  const remoteUserId = getRemoteCallPeerId(session, currentUserId)

  const cached = directoryPeer(remoteUserId, currentUserId)
  if (cached) return cached

  try {
    const user = await resolveDirectoryUser(remoteUserId)
    if (user?.displayName?.trim() && !isSelfDisplayName(user.displayName, currentUserId)) {
      return {
        userId: remoteUserId,
        displayName: user.displayName,
        avatarUrl: user.avatarUrl,
      }
    }
  } catch {
    /* roster lookup is best-effort */
  }

  return (
    sessionPeerIfRemote(session, remoteUserId, currentUserId) ?? {
      userId: remoteUserId,
      displayName: 'Unknown',
      avatarUrl: undefined,
    }
  )
}
